import dayjs from 'dayjs';
import fecha from 'fecha';
import { mapGetters } from 'vuex';
import { isValidDate, duplicateVar, isMobile } from '../lib/helper';

const defaultConfig = {};
const defaultI18n = 'ID';
const defaultWeekStart = 'monday';

const availableMonths = {
	EN: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
	ID: ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'],
};

const availableShortDays = {
	// EN: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
	EN: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
	// ID: ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'],
	ID: ['Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab', 'Min'],
};

const presetRangeLabel = {
	EN: {
		lifetime: 'Lifetime',
		yesterday: 'Yesterday',
		today: 'Today',
		thisMonth: 'This Month',
		lastMonth: 'Last Month',
		lastSevenDays: 'Last 7 Days',
		lastThirtyDays: 'Last 30 Days',
	},
	ID: {
		lifetime: 'Seumur Hidup',
		yesterday: 'Kemarin',
		today: 'Hari ini',
		thisMonth: 'Bulan ini',
		lastMonth: 'Bulan lalu',
		lastSevenDays: '7 Hari Terakhir',
		lastThirtyDays: '30 Hari Terakhir',
	},
};

const defaultCaptions = {
	apply: 'Apply',
	cancel: 'Cancel',
};

const defaultStyle = {
	daysWeeks: 'calendar-weeks',
	days: 'calendar-days',
	daysSelected: 'calendar-days-selected',
	daysInRange: 'calendar-days-in-range',
	firstDate: 'calendar-month-left',
	secondDate: 'calendar-month-right',
	presetRanges: 'calendar-preset-ranges',
	dateDisabled: 'calendar-days--disabled',
	dateInactive: 'calendar-days--inactive',
};

const defaultPresets = function (i18n = defaultI18n) {
	return {
		yesterday() {
			const n = new Date();
			const startToday = new Date(n.getFullYear(), n.getMonth(), n.getDate() - 1, 0, 0, 1);
			const endToday = new Date(n.getFullYear(), n.getMonth(), n.getDate() - 1, 23, 59, 59);
			return {
				label: presetRangeLabel[i18n].yesterday,
				active: false,
				dateRange: {
					start: startToday,
					end: endToday,
				},
			};
		},
		today() {
			const n = new Date();
			const startToday = new Date(n.getFullYear(), n.getMonth(), n.getDate(), 0, 0, 1);
			const endToday = new Date(n.getFullYear(), n.getMonth(), n.getDate(), 23, 59, 59);
			return {
				label: presetRangeLabel[i18n].today,
				active: false,
				dateRange: {
					start: startToday,
					end: endToday,
				},
			};
		},
		thisMonth() {
			const n = new Date();
			const startMonth = new Date(n.getFullYear(), n.getMonth(), 1);
			const endMonth = new Date(n.getFullYear(), n.getMonth() + 1, 0);
			return {
				label: presetRangeLabel[i18n].thisMonth,
				active: false,
				dateRange: {
					start: startMonth,
					end: endMonth,
				},
			};
		},
		lastMonth() {
			const n = new Date();
			const startMonth = new Date(n.getFullYear(), n.getMonth() - 1, 1);
			const endMonth = new Date(n.getFullYear(), n.getMonth(), 0);
			return {
				label: presetRangeLabel[i18n].lastMonth,
				active: false,
				dateRange: {
					start: startMonth,
					end: endMonth,
				},
			};
		},
		last7days() {
			const n = new Date();
			const start = new Date(n.getFullYear(), n.getMonth(), n.getDate() - 6);
			const end = new Date(n.getFullYear(), n.getMonth(), n.getDate());
			return {
				label: presetRangeLabel[i18n].lastSevenDays,
				active: false,
				dateRange: {
					start,
					end,
				},
			};
		},
		last30days() {
			const n = new Date();
			const start = new Date(n.getFullYear(), n.getMonth(), n.getDate() - 30);
			const end = new Date(n.getFullYear(), n.getMonth(), n.getDate());
			return {
				label: presetRangeLabel[i18n].lastThirtyDays,
				active: false,
				dateRange: {
					start,
					end,
				},
			};
		},
	};
};

// const maxRange = 365; // 1 year;

const today = new Date();
// const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 1);
// const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

export default {
	name: 'vue-rangedate-picker',
	props: {
		configs: {
			type: Object,
			default: () => defaultConfig,
		},
		i18n: {
			type: String,
			default: defaultI18n,
		},
		months: {
			type: Array,
			default: () => null,
		},
		shortDays: {
			type: Array,
			default: () => null,
		},
		weekStart: {
			type: String,
			default: defaultWeekStart,
		},
		// options for captions are: title, ok_button
		captions: {
			type: Object,
			default: () => defaultCaptions,
		},
		format: {
			type: String,
			default: 'DD MMM YYYY',
		},
		styles: {
			type: Object,
			default: () => {},
		},
		initRange: {
			type: Object,
			default: () => null,
		},
		startActiveMonth: {
			type: Number,
			default: new Date().getMonth(),
		},
		startActiveYear: {
			type: Number,
			default: new Date().getFullYear(),
		},
		presets: {
			type: Object,
			default: () => null,
		},
		compact: {
			type: Boolean,
			default: false,
		},
		righttoleft: {
			type: String,
			default: 'false',
		},
		start: {
			type: String,
		},
		end: {
			type: String,
		},
		isMaxToday: {
			type: Boolean,
			default: true,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			dateRange: {
				start: duplicateVar(startDate),
				end: duplicateVar(endDate),
			},
			numOfDays: 7,
			isFirstChoice: true,
			isOpen: false,
			presetActive: false,
			showMonth: false,
			activeMonthStart: this.startActiveMonth,
			activeYearStart: this.startActiveYear,
			activeYearEnd: this.startActiveYear,
		};
	},
	created() {
		window.addEventListener('keyup', this.onKeyup);
		if (this.activeMonthStart === 11) this.activeYearEnd = this.activeYearStart + 1;
		if (this.user) {
			this.checkDefaultPresets();
		}
	},
	destroyed() {
		window.removeEventListener('keyup', this.onKeyup);
	},
	watch: {
		startNextActiveMonth(value) {
			if (value === 0) this.activeYearEnd = this.activeYearStart + 1;
		},
		user() {
			this.checkDefaultPresets();
		},
	},
	computed: {
		...mapGetters({
			user: 'user',
		}),
		monthsLocale() {
			return this.months || availableMonths[this.i18n];
		},
		shortDaysLocale() {
			return this.shortDays || availableShortDays[this.i18n];
		},
		s() {
			return Object.assign({}, defaultStyle, this.style);
		},
		isStartMonday() {
			return this.weekStart === 'monday';
		},
		startMonthDay() {
			const date = new Date(this.activeYearStart, this.activeMonthStart, 1);
			const day = this.isStartMonday ? (date.getDay() + 6) % 7 : date.getDay();
			return day;
		},
		startNextMonthDay() {
			// set year to next year if start month is December (11 in value)
			const year = this.activeMonthStart === 11 ? this.activeYearStart + 1 : this.activeYearStart;
			const date = new Date(year, this.startNextActiveMonth, 1);
			const day = this.isStartMonday ? (date.getDay() + 6) % 7 : date.getDay();
			return day;
		},
		endMonthDate() {
			return new Date(this.activeYearEnd, this.startNextActiveMonth, 0).getDate();
		},
		endNextMonthDate() {
			return new Date(this.activeYearEnd, this.activeMonthStart + 2, 0).getDate();
		},
		startNextActiveMonth() {
			return this.activeMonthStart >= 11 ? 0 : this.activeMonthStart + 1;
		},
		finalPresetRanges() {
			const tmp = {};
			const presets = this.presets || defaultPresets(this.i18n);
			const presetsKeys = Object.keys(presets);
			for (let i = 0; i < presetsKeys.length; i++) {
				const key = presetsKeys[i];
				const item = presets[key];
				let plainItem = item;
				if (typeof item === 'function') {
					plainItem = item();
				}
				tmp[key] = plainItem;
			}
			return tmp;
		},
		isCompact() {
			return this.compact || isMobile();
		},
		isRighttoLeft() {
			return this.righttoleft === 'true';
		},
		label() {
			const start = this.getDateString(this.dateRange.start);
			const end = this.getDateString(this.dateRange.end);
			const label = end ? `${start} - ${end}` : start;
			return this.presetActive ? this.presetActive.label : label;
		},
	},
	methods: {
		toggleCalendar() {
			if (this.disabled === false) {
				this.isOpen = !this.isOpen;
				this.showMonth = !this.showMonth;
			}
		},
		getDateString(date, format = this.format) {
			if (!date) {
				return null;
			}
			const dateparse = new Date(Date.parse(date));
			return fecha.format(new Date(dateparse.getFullYear(), dateparse.getMonth(), dateparse.getDate()), format);
		},
		getDayIndexInMonth(r, i, startMonthDay) {
			const date = (this.numOfDays * (r - 1)) + i;
			const dayIndex = date - startMonthDay;
			return dayIndex;
		},
		getDayCell(r, i, startMonthDay, endMonthDate) {
			const result = this.getDayIndexInMonth(r, i, startMonthDay);
			// bound by > 0 and < last day of month
			return result > 0 && result <= endMonthDate ? result : '&nbsp;';
		},
		getClickedDate(result, activeMonth, activeYear) {
			const clickedDate = new Date(activeYear, activeMonth, result);
			return clickedDate;
		},
		getNewDateRange(result, activeMonth, activeYear) {
			const newData = {};
			let key = 'start';
			if (!this.isFirstChoice) {
				key = 'end';
			} else {
				newData.end = null;
			}
			const resultDate = new Date(activeYear, activeMonth, result);
			if (!this.isFirstChoice && resultDate < this.dateRange.start) {
				this.isFirstChoice = false;
				return { start: resultDate };
			}

			// toggle first choice
			this.isFirstChoice = !this.isFirstChoice;
			newData[key] = resultDate;
			return newData;
		},
		selectDate(r, i, day, month, year) {
			const result = this.getDayIndexInMonth(r, i, day);

			if (this.isFirstChoice) {
				this.dateRange.start = this.getClickedDate(result, month, year);
				this.dateRange.end = this.getClickedDate(result, month, year);
				this.isFirstChoice = false;
			} else {
				this.dateRange.end = this.getClickedDate(result, month, year);
				this.isFirstChoice = true;
			}

			this.presetActive = false;
		},
		selectFirstItem(r, i) {
			const result = this.getDayIndexInMonth(r, i, this.startMonthDay);
			this.dateRange = Object.assign({}, this.dateRange, this.getNewDateRange(result, this.activeMonthStart, this.activeYearStart));
			if (this.dateRange.start && this.dateRange.end) {
				this.presetActive = false;
				if (this.isCompact) {
					this.showMonth = false;
				}
			}
		},
		selectSecondItem(r, i) {
			const result = this.getDayIndexInMonth(r, i, this.startNextMonthDay);
			this.dateRange = Object.assign({}, this.dateRange, this.getNewDateRange(result, this.startNextActiveMonth, this.activeYearEnd));
			if (this.dateRange.start && this.dateRange.end) {
				this.presetActive = false;
			}
		},
		// isDateSelected(r, i, key, startMonthDay, endMonthDate) {
		isDateSelected(r, i, key, startMonthDay) {
			const result = this.getDayIndexInMonth(r, i, startMonthDay);

			let currDate = null;
			if (key === 'first') {
				currDate = new Date(this.activeYearStart, this.activeMonthStart, result);
			} else {
				currDate = new Date(this.activeYearEnd, this.startNextActiveMonth, result);
			}

			const isStartSame = (this.dateRange.start && this.dateRange.start.getFullYear() === currDate.getFullYear() && this.dateRange.start.getMonth() === currDate.getMonth() && this.dateRange.start.getDate() === currDate.getDate());
			const isEndSame = (this.dateRange.end && this.dateRange.end.getFullYear() === currDate.getFullYear() && this.dateRange.end.getMonth() === currDate.getMonth() && this.dateRange.end.getDate() === currDate.getDate());

			return isStartSame || isEndSame;
		},
		// isDateInRange(r, i, key, startMonthDay, endMonthDate) {
		isDateInRange(r, i, key, startMonthDay) {
			const result = this.getDayIndexInMonth(r, i, startMonthDay);

			let currDate = null;
			if (key === 'first') {
				currDate = new Date(this.activeYearStart, this.activeMonthStart, result);
			} else {
				currDate = new Date(this.activeYearEnd, this.startNextActiveMonth, result);
			}

			return (this.dateRange.start && this.dateRange.start.getTime() < currDate.getTime()) && (this.dateRange.end && this.dateRange.end.getTime() > currDate.getTime());
		},
		isDateDisabled(r, i, startMonthDay, endMonthDate) {
			const result = this.getDayIndexInMonth(r, i, startMonthDay);
			// bound by > 0 and < last day of month
			return !(result > 0 && result <= endMonthDate);
		},
		isDateInactive(r, i, key, startMonthDay, endMonthDate) {
			const result = this.getDayIndexInMonth(r, i, startMonthDay);

			// bound by > 0 and < last day of month
			const isDateEnabled = (result > 0 && result <= endMonthDate);

			const isInactive = false;
			let isPastToday = false;
			let isExceedMaxRange = false;

			if (isDateEnabled) {
				let currDate = null;
				if (key === 'first') {
					currDate = new Date(this.activeYearStart, this.activeMonthStart, result);
				} else {
					currDate = new Date(this.activeYearEnd, this.startNextActiveMonth, result);
				}

				isPastToday = dayjs(currDate).isAfter(dayjs(today));

				if (this.dateRange.start) {
					const oneYearAfterStart = dayjs(this.dateRange.start).add(1, 'year');
					isExceedMaxRange = dayjs(currDate).isAfter(oneYearAfterStart);
				}

				// const isStartSame = (this.dateRange.start && this.dateRange.start.getMonth() === currDate.getMonth() && this.dateRange.start.getDate() === currDate.getDate());
				// const isEndSame = (this.dateRange.end && this.dateRange.end.getMonth() === currDate.getMonth() && this.dateRange.end.getDate() === currDate.getDate());
			}

			// console.log(isDateEnabled, isPastToday, isInactive);

			const isDateInactive = isDateEnabled && ((this.isMaxToday && isPastToday) || isInactive || isExceedMaxRange);

			return isDateInactive;
		},
		goPrevMonth() {
			const prevMonth = new Date(this.activeYearStart, this.activeMonthStart, 0);
			this.activeMonthStart = prevMonth.getMonth();
			this.activeYearStart = prevMonth.getFullYear();
			this.activeYearEnd = prevMonth.getFullYear();
		},
		goNextMonth() {
			const nextMonth = new Date(this.activeYearEnd, this.startNextActiveMonth, 1);
			this.activeMonthStart = nextMonth.getMonth();
			this.activeYearStart = nextMonth.getFullYear();
			this.activeYearEnd = nextMonth.getFullYear();
		},
		updatePreset(item) {
			this.presetActive = item;
			this.dateRange = item.dateRange;
			// update start active month
			this.activeMonthStart = this.dateRange.start.getMonth();
			this.activeYearStart = this.dateRange.start.getFullYear();
			this.activeYearEnd = this.dateRange.end.getFullYear();
		},
		setDateValue() {
			this.$emit('selected', this.dateRange);
			this.toggleCalendar();
		},
		onKeyup(event) {
			if (event.key === 'Escape') {
				this.isOpen = false;
			}
		},
		checkDefaultPresets() {
			// Check Default Presets
			const keys = Object.keys(this.presets);
			for (let i = 0; i < keys.length; i++) {
				const key = keys[i];
				const preset = this.presets[key];
				if (preset.default) {
					this.presetActive = duplicateVar(preset);
					this.dateRange = duplicateVar(preset.dateRange);
					break;
				}
			}

			if (this.start && this.start !== '') {
				const start = this.moment(this.start).toDate();
				if (isValidDate(start)) {
					this.dateRange.start = start;
				}
			} else {
				this.dateRange.start = startDate;
			}

			if (this.end && this.end !== '') {
				const end = this.moment(this.end).toDate();
				if (isValidDate(end)) {
					this.dateRange.end = end;
				}
			} else {
				this.dateRange.end = endDate;
			}

			for (let i = 0; i < keys.length; i++) {
				const key = keys[i];
				const preset = this.presets[key];
				const start = preset.dateRange.start;
				const end = preset.dateRange.end;
				const isSameStart = this.moment(start).isSame(this.dateRange.start, 'day');
				const isSameEnd = this.moment(end).isSame(this.dateRange.end, 'day');
				if (isSameStart && isSameEnd) {
					this.presetActive = duplicateVar(preset);
					break;
				} else {
					this.presetActive = false;
				}
			}
		},
	},
};
